<script lang="ts">
	import SvgIcon from '@samsveltecomponents/core/Icons/SvgIcon.svelte';
	import { soundcloud, youtube } from '@samsveltecomponents/core/Icons/icons';
	import convertToSlug from '$utils/slugify';
	import { formatDatePair } from '$utils/formatDatePair';
	import { onMount } from 'svelte';

	import type { PageData } from './$types';
	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();
	let { worksGrouped, workGroupKeys } = $derived(data);

	let headingNavigationSidebar: HTMLElement;

	onMount(() => {
		// Intersection observer ScrollSpy (for contents highlighting)
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				const id = entry.target.getAttribute('id');
				// add the 'activeSection' class if it is intersecting
				if (entry.intersectionRatio > 0) {
					headingNavigationSidebar
						.querySelector(`li a[href="#${id}"]`)
						?.classList.add('active-section');
				} else {
					headingNavigationSidebar
						.querySelector(`li a[href="#${id}"]`)
						?.classList.remove('active-section');
				}
			});
		});
		// track all section elements with an id
		document.querySelectorAll('section[id]').forEach((section) => {
			observer.observe(section);
		});

		// unobserve all elements when the component is unmounted
		return () => {
			observer.disconnect();
		};
	});
</script>

<svelte:head>
	<title>Works</title>
	<meta name="description" content="Works by Sam Greening" />
</svelte:head>

<main class="min-w-0">
	<h1 class="page-title text-center text-8xl">Works</h1>
	{#if worksGrouped}
		<!-- eslint-disable-next-line svelte/require-each-key -->
		{#each workGroupKeys as workGroupKey}
			{@const workGroup = worksGrouped[workGroupKey]}

			<section class="flex flex-col" id={convertToSlug(workGroupKey)}>
				<h1 class="mb0 mx-[-12px] mt-2 border-b-[.125rem] border-current px-[12px] text-5xl">
					{workGroupKey}
				</h1>
				<ul class="list pa0 mt0 mb3 flex-grow">
					<!-- eslint-disable-next-line svelte/require-each-key -->
					{#each workGroup as work}
						<li>
							<ul class="pa0">
								<li class="mv2 db">
									<!-- eslint-disable svelte/no-navigation-without-resolve -->
									<a
										class="dark-gray hover-bg-washed-blue hide-child db w-100 min-h3 pv1 ph2 ma0 inline-flex items-center hover:text-blue-500"
										href="works/{work.slug.current}"
										data-sveltekit-preload-data
									>
										<div class="flex flex-col leading-tight">
											<div class="flex flex-row flex-wrap items-baseline">
												<div class="underline-child f3">{work.name}</div>
												{#if work.completionDate != undefined || work.startDate != undefined}
													<div class="ph2">
														({formatDatePair(work.startDate, work.completionDate)})
													</div>
												{/if}
												<div class="flex self-center">
													{#if work.extraLinks}
														<!-- eslint-disable-next-line svelte/require-each-key -->
														{#each work.extraLinks as link}
															<div class="px-1">
																{#if link === 'soundcloud'}
																	<SvgIcon icon={soundcloud} size="1.2rem" />
																{:else if link === 'youtube'}
																	<SvgIcon icon={youtube} size="1.2rem" />
																	<!-- {:else}
																	<span class="text-xs"><i>extra link</i></span> -->
																{/if}
															</div>
														{/each}
													{/if}
												</div>
											</div>
											{#if work.instrumentation}
												<div class="i">
													for {work.instrumentation}
												</div>
											{:else if work.summaryDetails}
												<div class="i">
													{work.summaryDetails}
												</div>
											{/if}
										</div>
										<div class="flex-grow"></div>
										<div class="child tr f3">&gt;</div>
									</a>
								</li>
							</ul>
						</li>
					{/each}
				</ul>
			</section>
		{/each}
	{:else}
		<p>No works found</p>
	{/if}
</main>

<div class="col-start-3 ml-10 mr-2 hidden lg:block">
	<nav class="sticky top-16 mt-[22rem]">
		<ul class="list pa0 mt0 mb3 flex-grow" bind:this={headingNavigationSidebar}>
			<!-- eslint-disable-next-line svelte/require-each-key -->
			{#each workGroupKeys as workGroupKey}
				<li class="my-1">
					<a class="contents-link text-link-main text-sm" href="#{convertToSlug(workGroupKey)}"
						>{workGroupKey}
					</a>
				</li>
			{/each}
		</ul>
	</nav>
</div>

<style>
	/* Class for the current heading in the contents sidebar */
	:global(nav .active-section) {
		color: rgb(var(--main-text-color));
	}

	.contents-link {
		color: rgb(var(--main-text-color) / 0.8);
	}
</style>
